import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { formatHot } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";
import DetailActions from "@/pages/detail/components/DetailActions";

export default function Detail() {
  const params = useParams();
  const movie = movies.find((m) => m.id === params.id);

  usePageMeta(
    movie ? `${movie.type === "movie" ? "电影" : "电视剧"}《${movie.title}》 - 国产久久久` : "影视详情 - 国产久久久",
    movie
      ? `${movie.title}（${movie.year}·${movie.region}）由${movie.director}执导，${movie.cast.join("、")}主演。${movie.summary}`
      : "国产久久久影视详情页。",
    movie ? `${movie.title},${movie.genres.join(",")},国产影视` : "国产久久久"
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.type === movie.type)
      .map((m) => ({
        item: m,
        overlap: m.genres.filter((g) => movie.genres.includes(g)).length,
      }))
      .sort((a, b) => {
        if (b.overlap !== a.overlap) return b.overlap - a.overlap;
        return a.item.updatedAt < b.item.updatedAt ? 1 : -1;
      })
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [movie]);

  const relatedNews = useMemo(() => newsList.slice(0, 3), []);

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          未找到该影视作品
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          您查看的作品可能已下架或链接有误，请返回首页重新浏览。
        </p>
        <Link
          to="/"
          className="inline-block mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const metaList = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类别", value: movie.genres.join(" · ") },
    { label: "上映年份", value: `${movie.year}` },
    { label: "时长", value: movie.duration },
    { label: "地区", value: movie.region },
  ];

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              {
                label: movie.type === "movie" ? "国产电影" : "国产电视剧",
                path: `/category/${movie.type}`,
              },
              { label: movie.title },
            ]}
          />
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-full md:w-64 lg:w-72 shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 国产久久久`}
                className="w-full h-full object-top object-cover"
              />
            </div>
            <div className="mt-4 rounded-lg bg-background-50 border border-background-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-500">综合评分</span>
                <span className="font-heading text-2xl font-bold text-primary-600">
                  {movie.rating.toFixed(1)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-foreground-500">热度</span>
                <span className="flex items-center gap-1 text-foreground-800">
                  <i className="ri-fire-line text-primary-400"></i>
                  {formatHot(movie.hot)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-foreground-500">更新时间</span>
                <span className="text-foreground-800">{movie.updatedAt}</span>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">
                {movie.type === "movie" ? "电影" : "电视剧"}
              </span>
              {movie.genres.map((genre) => (
                <span
                  key={genre}
                  className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs"
                >
                  {genre}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {movie.title}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {movie.summary}
            </p>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {metaList.map((item) => (
                <div
                  key={item.label}
                  className="flex gap-3 py-2 border-b border-background-200"
                >
                  <dt className="text-sm text-foreground-500 shrink-0 w-16">
                    {item.label}
                  </dt>
                  <dd className="text-sm text-foreground-800">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-7">
              <DetailActions movieId={movie.id} title={movie.title} />
            </div>
          </div>
        </div>

        <section className="mt-12">
          <SectionHeading title="剧情简介" />
          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              {movie.synopsis}
            </p>
            <p className="mt-4 text-xs text-foreground-400">
              温馨提示：本站仅提供影视内容展示与信息整理，不提供播放与下载服务，请通过官方正规渠道观看。
            </p>
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-12">
            <SectionHeading
              title="相关推荐"
              subtitle="根据类型与更新时间为您推荐"
              morePath={`/category/${movie.type}`}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <SectionHeading
            title="相关资讯"
            subtitle="与国产影视相关的最新报道与专题"
            morePath="/news"
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            {relatedNews.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}